import { useEffect, useMemo, useState } from "react";
import { CalendarIcon, ClockIcon, SearchIcon, SendIcon } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { QuillEditor, type QuillDelta } from "@/components/custom/quill-editor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BASE_URL } from "@/config/constants";
import type { ExtensionComponentProps } from "@/extensions/registry";
import { ApiRequestError, apiClient } from "@/lib/api/client";
import { assetsApi } from "@/lib/api/assets";
import { collectionDataApi, type CollectionDataItem } from "@/lib/api/collection-data";
import type { Asset } from "@/types/entities";

type TopicItem = {
  topic?: string;
};

type InsightItem = CollectionDataItem & {
  coverImage?: number | string | null;
  title?: string;
  slug?: string;
  author?: string;
  publishDate?: string;
  readTime?: number | string | null;
  topics?: TopicItem[] | string | null;
  content?: string;
};

type SortMode = "newest" | "oldest" | "title-asc" | "title-desc";

const ALL_TOPICS = "__all__";
const INSIGHTS_COLLECTION = "insights";
const NO_SHARE_ORDER = "__none__";
const SHARE_ORDERS = [1, 2, 3] as const;
const DEFAULT_BROADCAST_SUBJECT = "BNC Insight of the Week";
const DEFAULT_BROADCAST_HEADER = "This Week's Mindset Shift";
const EMPTY_DELTA: QuillDelta = { ops: [{ insert: "" }] };

type PublishResponse = {
  message?: string;
};

function normalizeText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function parseJson(value: string) {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function parseTopics(value: InsightItem["topics"]) {
  const rawTopics = typeof value === "string" ? parseJson(value) : value;
  if (!Array.isArray(rawTopics)) {
    return [];
  }

  return rawTopics
    .map((item) => normalizeText((item as TopicItem).topic))
    .filter(Boolean);
}

function extractDescription(content: unknown) {
  const parsedContent = typeof content === "string" ? parseJson(content) : content;
  const ops = parsedContent && typeof parsedContent === "object" && "ops" in parsedContent
    ? (parsedContent as { ops?: Array<{ insert?: unknown }> }).ops
    : null;

  if (!Array.isArray(ops)) {
    return typeof content === "string" ? content.replace(/<[^>]*>/g, "").trim() : "";
  }

  return ops
    .map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

function hasRichTextContent(delta: QuillDelta) {
  const text = delta.ops
    ?.map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("") ?? "";
  return text.trim().length > 0 || delta.ops?.some((op) => typeof op.insert === "object");
}

function getPublishErrorMessage(error: unknown) {
  if (error instanceof ApiRequestError) {
    const data = error.data;
    if (data && typeof data === "object" && "message" in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
    }
  }

  return error instanceof Error ? error.message : "Failed to publish insights";
}

function toAssetImageUrl(tenantSlug: string, coverImage: InsightItem["coverImage"], assetsById: Map<number, Asset>) {
  const assetId = Number(coverImage);
  if (!Number.isInteger(assetId) || assetId <= 0) {
    return "";
  }

  if (!assetsById.has(assetId)) {
    return "";
  }

  return `${BASE_URL}/tenants/${tenantSlug}/assets/${assetId}/content`;
}

function formatDate(value: string | undefined) {
  if (!value) {
    return "Unscheduled";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getRuntimeEndpoint(tenantSlug: string, extensionEndpointPath: string) {
  return `/tenants/${tenantSlug}/extensions${extensionEndpointPath.startsWith("/") ? "" : "/"}${extensionEndpointPath}`;
}

function sortInsights(items: InsightItem[], sortMode: SortMode) {
  return [...items].sort((a, b) => {
    if (sortMode === "title-asc") {
      return normalizeText(a.title).localeCompare(normalizeText(b.title));
    }

    if (sortMode === "title-desc") {
      return normalizeText(b.title).localeCompare(normalizeText(a.title));
    }

    const aDate = new Date(normalizeText(a.publishDate)).getTime();
    const bDate = new Date(normalizeText(b.publishDate)).getTime();
    const dateSort = (Number.isNaN(bDate) ? 0 : bDate) - (Number.isNaN(aDate) ? 0 : aDate);
    return sortMode === "newest" ? dateSort : -dateSort;
  });
}

export function BusinessNumberConsultingInsightsExtension({ tenantSlug, extension }: ExtensionComponentProps) {
  const [insights, setInsights] = useState<InsightItem[]>([]);
  const [assetsById, setAssetsById] = useState<Map<number, Asset>>(new Map());
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState(ALL_TOPICS);
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [broadcastSubject, setBroadcastSubject] = useState(DEFAULT_BROADCAST_SUBJECT);
  const [broadcastHeader, setBroadcastHeader] = useState(DEFAULT_BROADCAST_HEADER);
  const [broadcastBody, setBroadcastBody] = useState<QuillDelta>(EMPTY_DELTA);
  const [shareOrders, setShareOrders] = useState<Record<string, number>>({});
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    const loadInsights = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [items, assets] = await Promise.all([
          collectionDataApi.getAll(tenantSlug, INSIGHTS_COLLECTION),
          assetsApi.getAllByTenantSlug(tenantSlug),
        ]);

        setInsights(items as InsightItem[]);
        setAssetsById(new Map(assets.map((asset) => [asset.id, asset])));
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load insights");
      } finally {
        setIsLoading(false);
      }
    };

    void loadInsights();
  }, [tenantSlug]);

  const topics = useMemo(() => {
    const topicSet = new Set<string>();
    for (const insight of insights) {
      parseTopics(insight.topics).forEach((item) => topicSet.add(item));
    }
    return [...topicSet].sort((a, b) => a.localeCompare(b));
  }, [insights]);

  const filteredInsights = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = insights.filter((insight) => {
      const insightTopics = parseTopics(insight.topics);
      const description = extractDescription(insight.content);
      const haystack = [
        insight.title,
        insight.slug,
        insight.author,
        description,
        ...insightTopics,
      ].map(normalizeText).join(" ").toLowerCase();

      return (
        (!normalizedSearch || haystack.includes(normalizedSearch)) &&
        (topic === ALL_TOPICS || insightTopics.includes(topic))
      );
    });

    return sortInsights(filtered, sortMode);
  }, [insights, search, sortMode, topic]);

  const selectedInsights = useMemo(() => {
    return Object.entries(shareOrders)
      .map(([slug, order]) => ({ slug, order }))
      .sort((a, b) => a.order - b.order);
  }, [shareOrders]);

  const selectedInterestTopics = useMemo(() => {
    const selectedSlugs = new Set(selectedInsights.map((insight) => insight.slug));
    const topicSet = new Set<string>();

    for (const insight of insights) {
      const slug = normalizeText(insight.slug);
      if (!selectedSlugs.has(slug)) {
        continue;
      }

      parseTopics(insight.topics).forEach((item) => topicSet.add(item));
    }

    return [...topicSet].sort((a, b) => a.localeCompare(b));
  }, [insights, selectedInsights]);

  const handleShareOrderChange = (slug: string, value: string) => {
    setShareOrders((current) => {
      const next = { ...current };
      delete next[slug];

      if (value === NO_SHARE_ORDER) {
        return next;
      }

      const order = Number(value);
      if (!SHARE_ORDERS.includes(order as (typeof SHARE_ORDERS)[number])) {
        return current;
      }

      for (const [selectedSlug, selectedOrder] of Object.entries(next)) {
        if (selectedOrder === order) {
          delete next[selectedSlug];
        }
      }

      next[slug] = order;
      return next;
    });
  };

  const handlePublish = async () => {
    const subject = broadcastSubject.trim();
    const header = broadcastHeader.trim();

    if (selectedInsights.length === 0) {
      toast.error("Choose at least one insight");
      return;
    }

    if (!subject) {
      toast.error("Broadcast subject is required");
      return;
    }

    if (!header) {
      toast.error("Broadcast header is required");
      return;
    }

    if (!hasRichTextContent(broadcastBody)) {
      toast.error("Broadcast body is required");
      return;
    }

    setIsPublishing(true);
    try {
      const endpointPath = getRuntimeEndpoint(tenantSlug, extension.endpoint_path);
      const response = await apiClient.post<PublishResponse>(endpointPath, {
        subject,
        header,
        body: broadcastBody,
        interestTopics: selectedInterestTopics,
        insights: selectedInsights,
      });
      toast.success(response.message || (selectedInsights.length === 1 ? "Insight published" : "Insights published"));
    } catch (publishError) {
      toast.error(getPublishErrorMessage(publishError));
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="text-sm text-muted-foreground">Loading insights...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-4">
        <div className="text-sm text-destructive">{error}</div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="broadcast-subject">Subject</Label>
              <Input
                id="broadcast-subject"
                value={broadcastSubject}
                onChange={(event) => setBroadcastSubject(event.target.value)}
                placeholder="BNC Insight of the Week"
                disabled={isPublishing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="broadcast-header">Header</Label>
              <Input
                id="broadcast-header"
                value={broadcastHeader}
                onChange={(event) => setBroadcastHeader(event.target.value)}
                placeholder="This Week's Mindset Shift"
                disabled={isPublishing}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Body</Label>
            <QuillEditor
              value={broadcastBody}
              onChange={setBroadcastBody}
              tenantSlug={tenantSlug}
              disabled={isPublishing}
              placeholder="Write the broadcast introduction..."
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-md">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search insights"
            className="pl-9"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
          <Select value={topic} onValueChange={setTopic}>
            <SelectTrigger className="w-full sm:w-[190px]">
              <SelectValue placeholder="Topic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TOPICS}>All topics</SelectItem>
              {topics.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortMode} onValueChange={(value) => setSortMode(value as SortMode)}>
            <SelectTrigger className="w-full sm:w-[170px]">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="title-asc">Title A-Z</SelectItem>
              <SelectItem value="title-desc">Title Z-A</SelectItem>
            </SelectContent>
          </Select>

          <Button
            className="col-span-2 sm:col-span-1"
            onClick={() => void handlePublish()}
            disabled={isPublishing || selectedInsights.length === 0}
          >
            <SendIcon className="size-4" />
            {isPublishing ? "Publishing..." : `Publish ${selectedInsights.length || ""}`.trim()}
          </Button>
        </div>
      </div>

      {filteredInsights.length === 0 ? (
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">No insights found.</div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredInsights.map((insight) => {
            const slug = normalizeText(insight.slug);
            const description = extractDescription(insight.content);
            const insightTopics = parseTopics(insight.topics);
            const imageUrl = toAssetImageUrl(tenantSlug, insight.coverImage, assetsById);
            const readTime = insight.readTime ? `${insight.readTime} min` : "No estimate";
            const shareOrder = slug ? shareOrders[slug] : undefined;

            return (
              <Card key={insight.id} className="gap-0 overflow-hidden py-0">
                <div className="aspect-[16/9] bg-muted">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>

                <CardContent className="space-y-3 p-4">
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {insightTopics.slice(0, 3).map((item) => (
                        <Badge key={item} variant="secondary">
                          {item}
                        </Badge>
                      ))}
                    </div>
                    <h2 className="line-clamp-2 text-base font-semibold leading-6">
                      {insight.title || "Untitled insight"}
                    </h2>
                    <p className="line-clamp-3 text-sm leading-6 text-muted-foreground">
                      {description || "No description available."}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <CalendarIcon className="size-3.5" />
                      {formatDate(insight.publishDate)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="size-3.5" />
                      {readTime}
                    </span>
                  </div>
                </CardContent>

                <CardFooter className="border-t p-4">
                  <Select
                    value={shareOrder ? String(shareOrder) : NO_SHARE_ORDER}
                    onValueChange={(value) => handleShareOrderChange(slug, value)}
                    disabled={!slug || isPublishing}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Share order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_SHARE_ORDER}>Not shared</SelectItem>
                      {SHARE_ORDERS.map((order) => (
                        <SelectItem key={order} value={String(order)}>
                          Share {order}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
